import { useState, useEffect, useRef } from "react";
import { api, apiError, downloadBlob } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { UploadCloud, Trash2, Download, Loader2, Layers, MapPin, Sparkles } from "lucide-react";
import { cleanName } from "@/lib/format";

export default function GeologyPanel({ project, area, onClose, reload }) {
  const open = !!area;
  const [geo, setGeo] = useState(null);
  const [mapUri, setMapUri] = useState(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);
  const pid = project.id;
  const aid = area?.area_id;

  useEffect(() => {
    if (!open) { setGeo(null); setMapUri(null); return; }
    api.get(`/projects/${pid}/areas/${aid}/geology`)
      .then((r) => { setGeo(r.data.geology); setMapUri(r.data.map); })
      .catch(() => { setGeo(null); setMapUri(null); });
  }, [open, pid, aid]);

  const upload = async (files) => {
    const f = Array.from(files || [])[0];
    if (!f) return;
    if (!/\.(geojson|json|kml|kmz|zip)$/i.test(f.name)) return toast.error("Use GeoJSON / KML / KMZ / Shapefile .zip");
    setBusy(true);
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post(`/projects/${pid}/areas/${aid}/geology`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setGeo({ units: data.units, dominant: data.dominant, count: data.count, field: data.field, setting: data.setting });
      setMapUri(data.map);
      toast.success(`Geological map generated · ${data.count} unit(s)`);
      reload && reload();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const aiGenerate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/${pid}/areas/${aid}/geology/ai-generate`);
      setGeo({ units: data.units, dominant: data.dominant, count: data.count, field: data.field, setting: data.setting, ai_generated: true, source: data.source });
      setMapUri(data.map);
      toast.success(`AI geological map generated · ${data.count} indicative unit(s)`);
      reload && reload();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const del = async () => {
    try {
      await api.delete(`/projects/${pid}/areas/${aid}/geology`);
      setGeo(null); setMapUri(null); toast.success("Geology removed"); reload && reload();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-[92vw] w-[92vw] max-h-[92vh] p-0 overflow-hidden flex flex-col" data-testid="geology-dialog">
        <DialogTitle className="px-6 py-4 border-b border-border text-foreground font-bold shrink-0 flex items-center gap-2">
          <Layers size={18} className="text-primary" /> Local Geological Map - {aid} · {cleanName(area?.name)}
        </DialogTitle>

        <div className="bg-muted p-5 overflow-auto space-y-5">
          <div className="flex flex-wrap items-center gap-3">
            <input ref={inputRef} type="file" accept=".geojson,.json,.kml,.kmz,.zip" className="hidden"
              data-testid="geology-file-input" onChange={(e) => upload(e.target.files)} />
            <Button data-testid="geology-upload-btn" onClick={() => inputRef.current?.click()} disabled={busy}
              className="bg-primary hover:bg-primary/90 text-primary-foreground h-10 px-5 text-sm font-semibold">
              {busy ? <><Loader2 size={15} className="mr-2 animate-spin" />Processing…</> : <><UploadCloud size={15} className="mr-2" />{geo ? "Replace Geology" : "Upload Geology (SHP/GeoJSON/KML)"}</>}
            </Button>
            <Button data-testid="geology-ai-generate-btn" onClick={aiGenerate} disabled={busy} variant="outline"
              className="border-[#C20511] text-[#7a5b1e] hover:bg-[#C20511]/10 h-10 px-5 text-sm font-semibold">
              {busy ? <><Loader2 size={15} className="mr-2 animate-spin" />Generating…</> : <><Sparkles size={15} className="mr-2" />AI Auto-Detect &amp; Generate</>}
            </Button>
            {geo && (
              <>
                <Button data-testid="geology-download-btn" variant="outline" className="border-border h-10"
                  onClick={() => downloadBlob(`/projects/${pid}/areas/${aid}/geology/map`, `${aid}_geology.png`)}>
                  <Download size={15} className="mr-2" /> Download HD Map
                </Button>
                <Button data-testid="geology-delete-btn" variant="outline" className="border-red-200 text-red-600 h-10" onClick={del}>
                  <Trash2 size={15} className="mr-2" /> Remove
                </Button>
              </>
            )}
          </div>

          {geo?.ai_generated && (
            <div className="flex items-center gap-2 text-[11px] font-semibold text-red-800 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="geology-ai-badge">
              <Sparkles size={13} className="shrink-0" /> AI-interpreted · indicative regional geology - verify in the field before any use.
            </div>
          )}

          {!geo && !busy && (
            <div className="border-2 border-dashed border-border rounded-xl py-14 text-center text-muted-foreground" data-testid="geology-empty">
              <MapPin size={38} className="mx-auto mb-3 text-muted-foreground" />
              Upload a published or interpreted geological dataset - or tap <span className="font-semibold text-[#7a5b1e]">AI Auto-Detect &amp; Generate</span> to produce an indicative Local Geological Map and &ldquo;Local Geological Setting&rdquo; write-up from the licence location and target commodity.
            </div>
          )}

          {geo && (
            <div className="grid lg:grid-cols-2 gap-5" data-testid="geology-result">
              <div className="bg-card border border-border rounded-xl p-3">
                {mapUri
                  ? <img src={mapUri} alt="Local Geological Map" className="w-full rounded-md" data-testid="geology-map-img" />
                  : <div className="py-16 text-center text-muted-foreground">Map preview unavailable</div>}
              </div>
              <div className="space-y-4">
                <div className="bg-card border border-border rounded-xl p-4">
                  <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-semibold mb-2">Mapped Units ({geo.count || (geo.units || []).length})</div>
                  <div className="space-y-1.5" data-testid="geology-units">
                    {(geo.units || []).map((u, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm">
                        <span className="h-3.5 w-3.5 rounded-sm border border-border shrink-0" style={{ background: u.color }} />
                        <span className="text-foreground truncate flex-1">{u.name}</span>
                        <span className="text-muted-foreground text-xs tabular-nums">{typeof u.pct === "number" ? `${u.pct.toFixed(0)}%` : ""}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="bg-card border border-border rounded-xl p-4">
                  <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-semibold mb-2">Local Geological Setting</div>
                  <p className="text-sm text-foreground leading-relaxed" data-testid="geology-setting">{geo.setting}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
